import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import TimelineIcon from '@mui/icons-material/Timeline';
import EventIcon from '@mui/icons-material/Event';
import GroupsIcon from '@mui/icons-material/Groups';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import DownloadIcon from '@mui/icons-material/Download';
import IosShareIcon from '@mui/icons-material/IosShare';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PhotoInfoPanel from '../../components/common/PhotoInfoPanel';
import momentsApi from './momentsApi';
import profileApi from '../Profile/profileApi';
import { downloadUcOriginal, downloadUrlAsBlob, UcDownloadProgressBar } from '../../utils/ucOriginalDownload';
import { sharePhoto } from '../../utils/sharePhoto';

const LENSES = [
  { value: 'all', label: 'All Moments', helper: 'Every event in your journey' },
  { value: 'organised', label: 'Organised', helper: 'Events you created' },
  { value: 'attended', label: 'Attended', helper: 'Events you joined' },
  { value: 'requested', label: 'Requested', helper: 'Photos you asked for' },
  { value: 'shared', label: 'Circle Shared', helper: 'Shared through circles' },
  { value: 'specific', label: 'Specific events', helper: 'Pick individual events' },
];

const SORT_OPTIONS = [
  { value: 'recent', label: 'Most recent' },
  { value: 'newest', label: 'Most recent photos' },
  { value: 'popular', label: 'Popular' },
  { value: 'most_liked', label: 'Most liked' },
  { value: 'most_commented', label: 'Most commented' },
];

const VIEW_MODE_OPTIONS = [
  { value: 'events', label: 'Timeline' },
  { value: 'feeds', label: 'Feeds' },
  { value: 'grid', label: 'Grid' },
];

const EVENT_TYPE_COLORS = {
  Wedding: '#7C3AED',
  Birthday: '#F97316',
  Reception: '#0EA5E9',
  'Baby Shower': '#EC4899',
  'Corporate Meet': '#2563EB',
  Conference: '#0891B2',
  'Cultural Celebration': '#16A34A',
  'Family Gathering': '#DC2626',
};

function SearchModeIcon({ label, mode, onToggle }) {
  const isLegacy = mode === 'legacy';
  const Icon = isLegacy ? ManageSearchIcon : AutoAwesomeIcon;
  const modeLabel = isLegacy ? 'Legacy' : 'AI Planner';
  const title = label ? `Selected ${modeLabel}. ${label}` : `Selected ${modeLabel}`;

  return (
    <Tooltip title={title}>
      <Box
        component="button"
        type="button"
        onClick={onToggle}
        aria-label={title}
        sx={{
          width: 30,
          height: 30,
          px: 0,
          overflow: 'hidden',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 0.75,
          bgcolor: label ? 'background.paper' : 'grey.100',
          border: '1px solid',
          borderColor: label ? 'primary.light' : 'divider',
          color: label ? 'primary.main' : 'text.disabled',
          borderRadius: 999,
          cursor: 'pointer',
          boxShadow: label ? '0 4px 14px rgba(15, 23, 42, 0.12)' : 'none',
          transition: 'width 180ms ease, background-color 180ms ease, color 180ms ease',
          '&:hover': { width: 126, bgcolor: label ? 'primary.50' : 'grey.100' },
        }}
      >
        <Box sx={{ width: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 28px' }}>
          <Icon fontSize="small" />
        </Box>
        <Typography variant="caption" fontWeight={800} noWrap sx={{ lineHeight: 1 }}>
          {modeLabel}
        </Typography>
      </Box>
    </Tooltip>
  );
}

const MomentsSearchField = React.memo(function MomentsSearchField({ active, onSubmit, onClear, searchModeLabel, selectedSearchMode, onToggleSearchMode, onRefresh }) {
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  const submit = () => {
    onSubmit(draft.trim());
    inputRef.current?.blur();
  };
  const clear = () => {
    setDraft('');
    onClear();
    inputRef.current?.blur();
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: -14, right: 10, zIndex: 2 }}>
        <SearchModeIcon label={searchModeLabel} mode={selectedSearchMode} onToggle={onToggleSearchMode} />
      </Box>
      {onRefresh && (
        <Box sx={{ position: 'absolute', bottom: -14, right: 10, zIndex: 2, display: { xs: 'inline-flex', md: 'none' } }}>
          <Tooltip title="Refresh">
            <IconButton
              onClick={onRefresh}
              aria-label="Refresh"
              sx={{ width: 30, height: 30, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', boxShadow: 2 }}
            >
              <RefreshIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      <TextField
        fullWidth
        inputRef={inputRef}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            submit();
          }
        }}
        placeholder="Search your moments - people, scenes, events..."
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 999, bgcolor: 'background.paper' } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color={active ? 'primary' : 'action'} />
            </InputAdornment>
          ),
          endAdornment: draft ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={submit}
                disabled={!draft.trim()}
                aria-label="Search moments"
              >
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={clear} edge="end" aria-label="Clear moments search">
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : (
            <InputAdornment position="end">
              <Chip icon={<AutoAwesomeIcon />} label="AI search" size="small" color="primary" variant="outlined" />
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
});

function formatDate(value) {
  if (!value) return 'Date to be added';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date to be added';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatMonth(value) {
  if (!value) return 'Unscheduled';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unscheduled';
  return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

function timeAgo(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w`;
  return formatDate(value);
}

function photoKey(eventId, photoId) {
  return `${eventId}:${photoId}`;
}

function presignedMediaUrl(presignedUrls, type, eventId, photoId) {
  return presignedUrls?.[type]?.[photoKey(eventId, photoId)] || '';
}

function mergeMediaUrls(prev, next) {
  return {
    thumbnails: { ...(prev?.thumbnails || {}), ...(next?.thumbnails || {}) },
    previews: { ...(prev?.previews || {}), ...(next?.previews || {}) },
  };
}

function missingMediaPhotos(photos, mediaUrls, type) {
  const seen = new Set();
  return photos.filter((photo) => {
    const key = photoKey(photo.event_id, photo.photo_id);
    if (seen.has(key)) return false;
    seen.add(key);
    return !mediaUrls?.[type]?.[key];
  });
}

function PhotoMedia({ src, alt = '', loading = 'lazy', sx }) {
  if (!src) {
    return <Box aria-label={alt || undefined} sx={sx} />;
  }

  return <Box component="img" src={src} alt={alt} loading={loading} sx={sx} />;
}

// Renders the (already-available) low-res thumbnail as a blurred placeholder
// and crossfades in the full-res image only once it has fully loaded, so the
// slow top-to-bottom progressive paint of a large image is never visible.
function BlurUpPhoto({ thumbnailUrl, src, alt = '', sx }) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
  }, [src]);

  if (!thumbnailUrl) {
    return src ? <Box component="img" src={src} alt={alt} sx={sx} /> : null;
  }

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Box
        component="img"
        src={thumbnailUrl}
        alt=""
        aria-hidden="true"
        sx={{ ...sx, filter: 'blur(16px)', transform: 'scale(1.05)' }}
      />
      {src && (
        <Box
          component="img"
          src={src}
          alt={alt}
          onLoad={() => setLoaded(true)}
          sx={{
            ...sx,
            position: 'absolute',
            inset: 0,
            height: '100%',
            opacity: loaded ? 1 : 0,
            transition: 'opacity 0.4s ease',
          }}
        />
      )}
    </Box>
  );
}

function formatScoreValue(value, signed = false) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return signed ? '+0.000' : '0.000';
  return signed ? `${num >= 0 ? '+' : ''}${num.toFixed(3)}` : num.toFixed(3);
}

const ADDITIVE_SCORE_PARTS = [
  ['visual', 'Visual'],
  ['person', 'Person'],
  ['object', 'Object'],
  ['text', 'Text'],
  ['memory', 'Memory'],
  ['event_context', 'Event'],
];

const PLANNER_SIGNAL_PARTS = [
  ['person_score', 'Person signal'],
  ['visual_score', 'Visual signal'],
  ['text_score', 'Text signal'],
  ['event_bias', 'Event/place bias'],
];

function SearchScoreTooltip({ score }) {
  if (!score) return '';
  const parts = score.parts || {};
  const total = Number(score.total || 0);
  const isPlannerScore = Boolean(score.planner_used);
  const visibleParts = (isPlannerScore ? PLANNER_SIGNAL_PARTS : ADDITIVE_SCORE_PARTS)
    .map(([key, label]) => [key, label, Number(parts[key] || 0)])
    .filter(([, , value]) => Number.isFinite(value) && Math.abs(value) >= 0.0005);
  const rawRrf = Number(parts.rrf_raw || 0);

  return (
    <Box sx={{ p: 0.5, maxWidth: 300 }}>
      <Typography variant="caption" component="div" fontWeight={800}>
        {isPlannerScore ? 'Relevance' : 'Match score'} {formatScoreValue(total)}
      </Typography>

      {visibleParts.length > 0 ? (
        <Stack spacing={0.15} sx={{ mt: 0.5 }}>
          {visibleParts.map(([key, label, value]) => (
            <Typography key={key} variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
              {label}: {formatScoreValue(value, !isPlannerScore)}
            </Typography>
          ))}
        </Stack>
      ) : (
        <Typography variant="caption" component="div" sx={{ opacity: 0.78, mt: 0.5 }}>
          Ranked candidate score
        </Typography>
      )}

      {isPlannerScore && Number.isFinite(rawRrf) && rawRrf > 0 && (
        <Typography variant="caption" component="div" sx={{ opacity: 0.72, mt: 0.25 }}>
          Raw rank-fusion score {rawRrf.toFixed(5)}
        </Typography>
      )}

      {(score.notes || []).slice(0, 3).map((note) => (
        <Typography key={note} variant="caption" component="div" sx={{ opacity: 0.86, mt: 0.25 }}>
          {note}
        </Typography>
      ))}
    </Box>
  );
}

function StatTile({ icon, label, value }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          {icon}
        </Avatar>
        <Box>
          <Typography variant="h6" fontWeight={800} lineHeight={1}>{value}</Typography>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function EmptyMoments({ hasFilters, onReset }) {
  return (
    <Paper variant="outlined" sx={{ py: 7, px: 3, textAlign: 'center', borderRadius: 2, bgcolor: 'background.paper' }}>
      <TimelineIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
      <Typography variant="h6" fontWeight={700}>
        {hasFilters ? 'No moments in this lens yet' : 'Your event journey will appear here'}
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 560, mx: 'auto' }}>
        {hasFilters
          ? 'Choose another circle or perspective to see the events, requests, and shared memories available to you.'
          : 'Create or join an event, request your photos, or share grouped photos with a circle to start building Moments.'}
      </Typography>
      {hasFilters && (
        <Button variant="outlined" sx={{ mt: 3, textTransform: 'none' }} onClick={onReset}>
          Clear filters
        </Button>
      )}
    </Paper>
  );
}

function LensSelector({ lens, onChange, specificCount = 0, onSpecificClick }) {
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {LENSES.map((item) => {
        const selected = lens === item.value;
        const isSpecific = item.value === 'specific';
        const helper = isSpecific && specificCount > 0
          ? `${specificCount} event${specificCount === 1 ? '' : 's'} selected`
          : item.helper;
        return (
          <motion.div key={item.value} whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}>
            <Button
              variant={selected ? 'contained' : 'outlined'}
              onClick={() => (isSpecific ? onSpecificClick() : onChange(item.value))}
              sx={{ borderRadius: 999, textTransform: 'none', px: 2, py: 1, boxShadow: selected ? 3 : 0 }}
            >
              <Box component="span" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.1 }}>
                <Typography component="span" variant="body2" fontWeight={800}>{item.label}</Typography>
                <Typography component="span" variant="caption" sx={{ opacity: selected ? 0.85 : 0.65 }}>{helper}</Typography>
              </Box>
            </Button>
          </motion.div>
        );
      })}
    </Stack>
  );
}

function categorizeSpecificEventSections(moments) {
  const organised = [];
  const attended = [];
  const requested = [];
  const shared = [];
  for (const m of moments) {
    if (m.role === 'organiser') organised.push(m);
    if (m.role === 'attendee') attended.push(m);
    if ((m.my_request_count || 0) > 0) requested.push(m);
    if ((m.circles || []).length > 0) shared.push(m);
  }
  return [
    { key: 'organised', label: 'Organised', items: organised },
    { key: 'attended', label: 'Attended', items: attended },
    { key: 'requested', label: 'Requested', items: requested },
    { key: 'shared', label: 'Circle Shared', items: shared },
  ].filter((section) => section.items.length > 0);
}

function SpecificEventsDialog({ open, onClose, moments, loading, initialSelected, onConfirm }) {
  const [selected, setSelected] = useState(() => new Set(initialSelected));
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    setSelected(new Set(initialSelected));
    setQuery('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const sections = useMemo(() => categorizeSpecificEventSections(moments), [moments]);
  const filteredSections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sections;
    return sections
      .map((section) => ({ ...section, items: section.items.filter((m) => (m.event_name || '').toLowerCase().includes(q)) }))
      .filter((section) => section.items.length > 0);
  }, [sections, query]);

  const toggle = (eventId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Choose specific events
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search events..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </Box>
        {loading ? (
          <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : filteredSections.length === 0 ? (
          <Typography color="text.secondary" sx={{ px: 2, py: 4, textAlign: 'center' }}>
            No events found
          </Typography>
        ) : (
          <List dense sx={{ maxHeight: 420, overflowY: 'auto', pb: 0 }}>
            {filteredSections.map((section) => (
              <React.Fragment key={section.key}>
                <ListSubheader sx={{ fontWeight: 800 }}>{section.label}</ListSubheader>
                {section.items.map((m) => (
                  <ListItemButton key={`${section.key}-${m.event_id}`} onClick={() => toggle(m.event_id)}>
                    <Checkbox edge="start" checked={selected.has(m.event_id)} tabIndex={-1} disableRipple sx={{ mr: 1 }} />
                    <ListItemText primary={m.event_name} secondary={formatDate(m.event_date)} />
                  </ListItemButton>
                ))}
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          {selected.size} event{selected.size === 1 ? '' : 's'} selected
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={() => onConfirm(Array.from(selected))} sx={{ textTransform: 'none' }}>
            Apply
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

function CircleSelector({ circles, selectedIds, onChange }) {
  const selectedSet = new Set(selectedIds);
  const toggleCircle = (circleId) => {
    if (selectedSet.has(circleId)) {
      onChange(selectedIds.filter((id) => id !== circleId));
    } else {
      onChange([...selectedIds, circleId]);
    }
  };

  return (
    <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap alignItems="center">
      <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
        <Button
          variant={!selectedIds.length ? 'contained' : 'outlined'}
          onClick={() => onChange([])}
          sx={{ borderRadius: 999, textTransform: 'none', minHeight: 44 }}
          startIcon={<GroupsIcon />}
        >
          All Circles
        </Button>
      </motion.div>
      {circles.map((circle) => {
        const selected = selectedSet.has(circle.circle_id);
        return (
          <motion.button
            key={circle.circle_id}
            type="button"
            whileHover={{ scale: 1.06, rotate: -1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => toggleCircle(circle.circle_id)}
            style={{
              border: selected ? `3px solid ${circle.color || '#1976d2'}` : '1px solid rgba(0,0,0,0.16)',
              background: selected ? '#fff' : 'rgba(255,255,255,0.8)',
              borderRadius: 999,
              cursor: 'pointer',
              padding: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              minHeight: 48,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 34, height: 34, borderRadius: '50%',
                background: circle.color || '#4ECDC4',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontWeight: 800,
              }}
            >
              {(circle.circle_name || 'C').slice(0, 1).toUpperCase()}
            </span>
            <Typography component="span" variant="body2" fontWeight={700} sx={{ pr: 1.25 }}>
              {circle.circle_name}
              {selected && circle.photo_count ? ` (${circle.photo_count})` : ''}
            </Typography>
          </motion.button>
        );
      })}
    </Stack>
  );
}

function SortMenuButton({ sortMode, onChange, sx }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const active = sortMode !== 'recent';
  return (
    <>
      <Tooltip title="Sort photos">
        <IconButton
          onClick={(event) => setAnchorEl(event.currentTarget)}
          color={active ? 'primary' : 'default'}
          aria-label="Sort photos"
          sx={{
            border: '1px solid',
            borderColor: active ? 'primary.main' : 'divider',
            bgcolor: active ? 'primary.50' : 'background.paper',
            ...sx,
          }}
        >
          <SortIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {SORT_OPTIONS.map((opt) => (
          <MenuItem
            key={opt.value}
            selected={sortMode === opt.value}
            onClick={() => { onChange(opt.value); setAnchorEl(null); }}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function ViewModeToggle({ viewMode, onChange, fullWidth = false, compact = false }) {
  return (
    <Box
      role="tablist"
      aria-label="Moments view"
      sx={{
        display: 'flex',
        gap: 0.5,
        p: 0.5,
        bgcolor: 'action.hover',
        borderRadius: 999,
        width: fullWidth ? '100%' : 'fit-content',
      }}
    >
      {VIEW_MODE_OPTIONS.map((opt) => {
        const selected = viewMode === opt.value;
        return (
          <Box
            key={opt.value}
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(opt.value)}
            sx={{
              flex: fullWidth ? 1 : '0 0 auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: compact ? 1.25 : 2,
              py: compact ? 0.5 : 0.85,
              borderRadius: 999,
              cursor: 'pointer',
              userSelect: 'none',
              bgcolor: selected ? 'background.paper' : 'transparent',
              boxShadow: selected ? 1 : 0,
              transition: 'background-color .15s ease, box-shadow .15s ease',
              '&:hover': { bgcolor: selected ? 'background.paper' : 'action.selected' },
            }}
          >
            <Typography
              variant={compact ? 'caption' : 'body2'}
              noWrap
              sx={{ fontWeight: selected ? 800 : 600, color: selected ? 'primary.main' : 'text.secondary' }}
            >
              {selected ? `${opt.label} view` : opt.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function PhotoGrid({ moment, onPhotoClick, photoLimit = 20, onShowMorePhotos, presignedUrls = {} }) {
  const photoIds = moment.photo_ids || moment.sample_photo_ids || [];
  const visibleIds = photoIds.slice(0, photoLimit);
  const remaining = photoIds.length - photoLimit;
  const searchScores = moment.search_scores || {};

  if (!visibleIds.length) {
    const grouped = moment.grouping_status === 'complete';
    const hasPhotos = (moment.photo_count || 0) > 0;
    const message = grouped
      ? hasPhotos
        ? 'No photos match the current filter or search query.'
        : 'No photos are available for this moment.'
      : 'Photos appear after grouping is complete';
    return (
      <Box sx={{ height: 140, borderRadius: 2, bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
        <Stack alignItems="center" spacing={0.5}>
          <PhotoLibraryIcon />
          <Typography variant="caption">{message}</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Stack spacing={1}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(4, 1fr)', md: 'repeat(6, 1fr)' }, gap: 1 }}>
        {visibleIds.map((photoId, index) => {
          const score = searchScores[photoId];
          const thumbnailUrl = presignedMediaUrl(presignedUrls, 'thumbnails', moment.event_id, photoId);
          const loading = index < 16 ? 'eager' : 'lazy';
          const button = (
            <motion.button
              key={`${moment.event_id}-${photoId}`}
              type="button"
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: Math.min(index * 0.015, 0.3) }}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onPhotoClick(moment, photoId)}
              style={{ border: 0, padding: 0, background: 'transparent', cursor: 'pointer', borderRadius: 8, overflow: 'hidden' }}
            >
              <PhotoMedia
                src={thumbnailUrl}
                alt=""
                loading={loading}
                sx={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', display: 'block', bgcolor: 'grey.100' }}
              />
            </motion.button>
          );
          return score ? (
            <Tooltip key={`${moment.event_id}-${photoId}`} title={<SearchScoreTooltip score={score} />} arrow placement="top">
              <Box component="span" sx={{ display: 'block' }}>{button}</Box>
            </Tooltip>
          ) : button;
        })}
      </Box>
      {remaining > 0 && (
        <Button
          size="small"
          onClick={() => onShowMorePhotos(photoIds.length)}
          sx={{ textTransform: 'none', alignSelf: 'flex-start', color: 'text.secondary' }}
        >
          Show {Math.min(20, remaining)} more photos ({remaining} remaining)
        </Button>
      )}
    </Stack>
  );
}

function MomentPanel({ moment, onPhotoClick, onOpenEvent, onHide, photoLimit, onShowMorePhotos, presignedUrls }) {
  const color = EVENT_TYPE_COLORS[moment.event_type] || '#4ECDC4';
  const sharedCount = moment.circles?.length || 0;

  return (
    <motion.div layout initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, bgcolor: 'background.paper' }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip label={moment.role === 'organiser' ? 'You organised' : 'You attended'} color={moment.role === 'organiser' ? 'primary' : 'default'} size="small" />
                {moment.event_type && <Chip label={moment.event_type} size="small" variant="outlined" sx={{ borderColor: color, color }} />}
                {moment.grouping_status === 'complete' && <Chip label="Photos ready" size="small" color="success" variant="outlined" />}
              </Stack>
              <Typography variant="h6" fontWeight={850} sx={{ mt: 1, lineHeight: 1.2 }}>
                {moment.event_name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {moment.organiser_name ? `Organiser: ${moment.organiser_name}` : 'Organiser details unavailable'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title={moment.hidden ? 'Show in Moments' : 'Hide from Moments'}>
                <IconButton size="small" onClick={() => onHide(moment)}>
                  {moment.hidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
                </IconButton>
              </Tooltip>
              <Button variant="outlined" endIcon={<OpenInNewIcon />} onClick={() => onOpenEvent(moment)} sx={{ textTransform: 'none', borderRadius: 2 }}>
                Organise in event
              </Button>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip icon={<PhotoLibraryIcon />} label={`${moment.photo_count || 0} photos`} size="small" variant="outlined" />
            <Chip icon={<HowToRegIcon />} label={`${moment.attendee_count || 0} attendees`} size="small" variant="outlined" />
            <Chip icon={<PersonSearchIcon />} label={`${moment.request_count || 0} requests`} size="small" variant="outlined" />
            {sharedCount > 0 && <Chip icon={<GroupsIcon />} label={`${sharedCount} circles`} size="small" variant="outlined" />}
          </Stack>

          {moment.circles?.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {moment.circles.map((circle) => (
                <Chip
                  key={circle.circle_id}
                  label={circle.circle_name}
                  size="small"
                  sx={{ bgcolor: circle.color || 'grey.100', color: 'common.white', fontWeight: 750 }}
                />
              ))}
            </Stack>
          )}

          <PhotoGrid
            moment={moment}
            onPhotoClick={onPhotoClick}
            photoLimit={photoLimit}
            onShowMorePhotos={onShowMorePhotos}
            presignedUrls={presignedUrls}
          />

          <Stack spacing={0.5}>
            {(moment.visibility_reasons || []).map((reason) => (
              <Typography key={reason} variant="caption" color="text.secondary">{reason}</Typography>
            ))}
          </Stack>
        </Stack>
      </Paper>
    </motion.div>
  );
}

function GridPhotoView({ photos, visibleCount, onLoadMore, onPhotoClick, hasFilters, onReset, searchActive, searchQuery, searchModeLabel, searchModeNote, presignedUrls }) {
  const visiblePhotos = photos.slice(0, visibleCount);
  const remaining = photos.length - visibleCount;

  if (!photos.length) {
    return <EmptyMoments hasFilters={hasFilters} onReset={onReset} />;
  }

  return (
    <Stack spacing={3} sx={{ mt: 3 }}>
      {searchActive && searchQuery && (
        <Box sx={{ ml: 0.5 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <Typography variant="caption" color="primary">
              Showing top {photos.length} matches for <strong>{searchQuery}</strong> across all events
            </Typography>
            {searchModeLabel && <Chip label={searchModeLabel} size="small" color="primary" variant="outlined" />}
          </Stack>
          {searchModeNote && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.25 }}>
              {searchModeNote}
            </Typography>
          )}
        </Box>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(5, 1fr)', md: 'repeat(8, 1fr)' },
          gap: 0.5,
        }}
      >
        {visiblePhotos.map((photo, index) => {
          const thumbnailUrl = photo.thumbnail_url || presignedMediaUrl(presignedUrls, 'thumbnails', photo.event_id, photo.photo_id);
          const loading = index < 16 ? 'eager' : 'lazy';
          const button = (
            <motion.button
              key={`${photo.event_id}-${photo.photo_id}`}
              type="button"
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: Math.min(index * 0.008, 0.4) }}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onPhotoClick(photo)}
              style={{ border: 0, padding: 0, background: 'transparent', cursor: 'pointer', overflow: 'hidden' }}
            >
              <PhotoMedia
                src={thumbnailUrl}
                alt=""
                loading={loading}
                sx={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', display: 'block', bgcolor: 'grey.100' }}
              />
            </motion.button>
          );
          return photo.search_score ? (
            <Tooltip key={`${photo.event_id}-${photo.photo_id}`} title={<SearchScoreTooltip score={photo.search_score} />} arrow placement="top">
              <Box component="span" sx={{ display: 'block' }}>{button}</Box>
            </Tooltip>
          ) : button;
        })}
      </Box>
      {remaining > 0 && (
        <Box sx={{ textAlign: 'center' }}>
          <Button variant="outlined" onClick={onLoadMore} sx={{ textTransform: 'none', borderRadius: 999 }}>
            Load {Math.min(50, remaining)} more photos ({remaining} remaining)
          </Button>
        </Box>
      )}
    </Stack>
  );
}

const VISIBILITY_LABELS = { public: 'Public', organiser: 'Organiser only' };

function LikersPopover({ anchorEl, onClose, likers, loading }) {
  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      disableRestoreFocus
      disableScrollLock
      disableAutoFocus
      disableEnforceFocus
      hideBackdrop
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ pointerEvents: 'none' }}
      slotProps={{ paper: { sx: { pointerEvents: 'auto', p: 1, maxWidth: 260 } } }}
    >
      <Stack spacing={0.75}>
        {loading ? (
          [0, 1, 2].map((i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="center">
              <Skeleton variant="circular" width={24} height={24} />
              <Skeleton variant="text" width={100} />
            </Stack>
          ))
        ) : likers.length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>No likes yet</Typography>
        ) : (
          likers.map((liker) => (
            <Stack key={liker.customer_id} direction="row" spacing={1} alignItems="center">
              <Avatar src={liker.avatar || undefined} sx={{ width: 24, height: 24, fontSize: 12 }}>
                {(liker.name || '?').slice(0, 1).toUpperCase()}
              </Avatar>
              <Typography variant="caption" fontWeight={600} noWrap>{liker.name}</Typography>
            </Stack>
          ))
        )}
      </Stack>
    </Popover>
  );
}

const LIKERS_PAGE_SIZE = 100;

function LikersDialog({ open, onClose, eventId, photoId, totalHint }) {
  const [likers, setLikers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(totalHint || 0);
  const sentinelRef = useRef(null);
  const offsetRef = useRef(0);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMoreRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const payload = await momentsApi.getLikers(eventId, photoId, {
        limit: LIKERS_PAGE_SIZE, offset: offsetRef.current,
      });
      const page = Array.isArray(payload.likers) ? payload.likers : [];
      setLikers((prev) => [...prev, ...page]);
      offsetRef.current += page.length;
      setTotal(payload.total || 0);
      hasMoreRef.current = Boolean(payload.has_more);
      setHasMore(hasMoreRef.current);
    } catch {
      hasMoreRef.current = false;
      setHasMore(false);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [eventId, photoId]);

  // Reset and load the first page each time the dialog opens for a photo.
  useEffect(() => {
    if (!open) return undefined;
    setLikers([]);
    offsetRef.current = 0;
    hasMoreRef.current = true;
    setHasMore(true);
    loadMore();
    return undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, eventId, photoId]);

  // Auto-load the next page of 100 as the user scrolls near the bottom.
  useEffect(() => {
    if (!open) return undefined;
    const node = sentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) loadMore(); },
      { threshold: 0.1 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [open, loadMore, likers.length]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography fontWeight={800}>Liked by{total ? ` ${total}` : ''}</Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
      <DialogContent sx={{ p: 0, maxHeight: '60vh', overflowY: 'auto' }}>
        {likers.map((liker) => (
          <Stack key={liker.customer_id} direction="row" spacing={1.5} alignItems="center" sx={{ px: 2, py: 1 }}>
            <Avatar src={liker.avatar || undefined} sx={{ width: 36, height: 36 }}>
              {(liker.name || '?').slice(0, 1).toUpperCase()}
            </Avatar>
            <Typography variant="body2" fontWeight={600}>{liker.name}</Typography>
          </Stack>
        ))}
        {loading && [0, 1, 2].map((i) => (
          <Stack key={`skeleton-${i}`} direction="row" spacing={1.5} alignItems="center" sx={{ px: 2, py: 1 }}>
            <Skeleton variant="circular" width={36} height={36} />
            <Skeleton variant="text" width={140} />
          </Stack>
        ))}
        {!loading && likers.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 4, textAlign: 'center' }}>
            No likes yet
          </Typography>
        )}
        <Box ref={sentinelRef} sx={{ height: 1 }} />
      </DialogContent>
    </Dialog>
  );
}

function CommentRow({ comment, onDelete }) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ py: 0.5 }}>
      <Avatar src={comment.author_avatar || undefined} sx={{ width: 26, height: 26, fontSize: 12, mt: 0.25 }}>
        {(comment.author_name || '?').slice(0, 1).toUpperCase()}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={0.75} alignItems="baseline" flexWrap="wrap" useFlexGap>
          <Typography variant="caption" fontWeight={800}>{comment.author_name}</Typography>
          <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>{comment.comment_text}</Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
            {timeAgo(comment.created_at)}
          </Typography>
          {comment.visibility_scope !== 'public' && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, fontStyle: 'italic' }}>
              {VISIBILITY_LABELS[comment.visibility_scope] || 'Circle only'}
            </Typography>
          )}
        </Stack>
      </Box>
      {comment.can_delete && (
        <IconButton size="small" onClick={() => onDelete(comment.comment_id)} aria-label="Delete comment" sx={{ p: 0.5 }}>
          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Stack>
  );
}

function CommentsPanel({ eventId, photoId, availableCircles, onCommentCountDelta }) {
  const [comments, setComments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [scope, setScope] = useState('public');
  const [circleId, setCircleId] = useState('');
  const [circles, setCircles] = useState(availableCircles || []);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    momentsApi.getComments(eventId, photoId)
      .then((payload) => {
        if (cancelled) return;
        setComments(Array.isArray(payload.comments) ? payload.comments : []);
        setCircles(Array.isArray(payload.available_circles) ? payload.available_circles : []);
      })
      .catch(() => { if (!cancelled) setComments([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, photoId]);

  const scopeOptions = [
    { value: 'public', label: 'Public' },
    { value: 'organiser', label: 'Organiser' },
    ...circles.map((c) => ({ value: c.circle_id, label: c.circle_name, isCircle: true })),
  ];
  const selectedValue = scope === 'circle' ? circleId : scope;

  const handleScopeSelect = (value) => {
    const isCircle = circles.some((c) => c.circle_id === value);
    if (isCircle) {
      setScope('circle');
      setCircleId(value);
    } else {
      setScope(value);
      setCircleId('');
    }
  };

  const handleSubmit = async () => {
    const text = draft.trim();
    if (!text || submitting) return;
    setSubmitting(true);
    try {
      const result = await momentsApi.postComment(eventId, photoId, {
        commentText: text,
        visibilityScope: scope,
        visibilityCircleId: scope === 'circle' ? circleId : undefined,
      });
      setComments((prev) => ([...(prev || []), {
        comment_id: result.comment_id,
        comment_text: text,
        visibility_scope: scope,
        visibility_circle_id: scope === 'circle' ? circleId : null,
        created_at: new Date().toISOString(),
        author_name: 'You',
        can_delete: true,
      }]));
      setDraft('');
      onCommentCountDelta(1);
    } catch (err) {
      // best-effort — comment box keeps the draft so the user can retry
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    const prev = comments;
    setComments((cur) => (cur || []).filter((c) => c.comment_id !== commentId));
    try {
      await momentsApi.deleteComment(eventId, photoId, commentId);
      onCommentCountDelta(-1);
    } catch (err) {
      setComments(prev);
    }
  };

  return (
    <Box sx={{ px: 1.25, pb: 1, pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
      {loading ? (
        <Stack spacing={0.75} sx={{ py: 1 }}>
          {[0, 1].map((i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="center">
              <Skeleton variant="circular" width={26} height={26} />
              <Skeleton variant="text" width="70%" />
            </Stack>
          ))}
        </Stack>
      ) : comments.length === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', py: 1 }}>
          No comments yet — be the first to say something.
        </Typography>
      ) : (
        <Stack divider={<Divider flexItem />} sx={{ maxHeight: 260, overflowY: 'auto' }}>
          {comments.map((comment) => (
            <CommentRow key={comment.comment_id} comment={comment} onDelete={handleDelete} />
          ))}
        </Stack>
      )}

      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1, mb: 0.75 }}>
        {scopeOptions.map((opt) => (
          <Chip
            key={opt.value}
            label={opt.label}
            size="small"
            variant={selectedValue === opt.value ? 'filled' : 'outlined'}
            color={selectedValue === opt.value ? 'primary' : 'default'}
            onClick={() => handleScopeSelect(opt.value)}
            sx={{ height: 22, fontSize: 11 }}
          />
        ))}
      </Stack>

      <Stack direction="row" spacing={0.75} alignItems="center">
        <TextField
          size="small"
          fullWidth
          placeholder="Add a comment..."
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSubmit(); } }}
          inputProps={{ maxLength: 1000 }}
        />
        <IconButton onClick={handleSubmit} disabled={!draft.trim() || submitting} aria-label="Post comment">
          <SendIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
}

function FeedCard({ photo, momentInfo, previewUrl, thumbnailUrl, interaction, onPhotoClick, onOpenEvent, onToggleLike, onToggleBookmark, onCommentCountDelta }) {
  const navigate = useNavigate();
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [likersAnchor, setLikersAnchor] = useState(null);
  const [likers, setLikers] = useState(null);
  const [likersLoading, setLikersLoading] = useState(false);
  const [likersDialogOpen, setLikersDialogOpen] = useState(false);

  const liked = Boolean(interaction?.liked_by_me);
  const bookmarked = Boolean(interaction?.bookmarked_by_me);
  const likeCount = interaction?.like_count ?? 0;
  const commentCount = interaction?.comment_count ?? 0;
  const loaded = Boolean(interaction);

  const [heartBurst, setHeartBurst] = useState(false);
  const tapTimer = useRef(null);
  useEffect(() => () => clearTimeout(tapTimer.current), []);

  const handlePhotoTap = () => {
    if (tapTimer.current) {
      // Second tap within the window - treat as a double-tap (Instagram-style
      // like), not two separate single-taps opening the preview.
      clearTimeout(tapTimer.current);
      tapTimer.current = null;
      if (!liked) onToggleLike(photo.event_id, photo.photo_id);
      setHeartBurst(true);
      setTimeout(() => setHeartBurst(false), 700);
      return;
    }
    tapTimer.current = setTimeout(() => {
      tapTimer.current = null;
      onPhotoClick(photo);
    }, 250);
  };

  const openLikers = (anchorEl) => {
    setLikersAnchor(anchorEl);
    // Always refetch — a cached empty result from a hover that raced ahead of
    // (or happened before) a like toggle would otherwise stick forever, since
    // nothing else in this card ever invalidates a stale `likers` list.
    setLikersLoading(true);
    momentsApi.getLikers(photo.event_id, photo.photo_id)
      .then((payload) => setLikers(Array.isArray(payload.likers) ? payload.likers : []))
      .catch(() => setLikers((prev) => prev || []))
      .finally(() => setLikersLoading(false));
  };

  const handleDownload = () => {
    downloadUrlAsBlob(
      momentsApi.originalUrl(photo.event_id, photo.photo_id),
      `circlemoments-${photo.event_id}-${photo.photo_id}.jpg`,
    ).catch((err) => window.alert(err.message || 'Download failed.'));
  };

  const authorName = momentInfo?.organiser_name || 'Organiser';
  const authorUsername = momentInfo?.organiser_username;

  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: { xs: 0, sm: 2 }, overflow: 'hidden', bgcolor: 'background.paper' }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1, py: 0.75 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Avatar src={momentInfo?.organiser_avatar || undefined} sx={{ width: 30, height: 30, fontSize: 13 }}>
            {authorName.slice(0, 1).toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.5} alignItems="baseline" flexWrap="wrap" useFlexGap sx={{ minWidth: 0, rowGap: 0 }}>
              <Typography
                variant="body2"
                fontWeight={800}
                noWrap
                component="span"
                onClick={authorUsername ? () => navigate(`/profile/${authorUsername}`) : undefined}
                sx={{
                  cursor: authorUsername ? 'pointer' : 'default',
                  '&:hover': authorUsername ? { textDecoration: 'underline' } : undefined,
                }}
              >
                {authorName}
              </Typography>
              {photo.submitted_by_name && photo.submitted_by_name !== authorName && (
                <>
                  <Typography variant="body2" color="text.secondary" component="span">and</Typography>
                  <Typography
                    variant="body2"
                    fontWeight={800}
                    noWrap
                    component="span"
                    onClick={photo.submitted_by_username ? () => navigate(`/profile/${photo.submitted_by_username}`) : undefined}
                    sx={{
                      cursor: photo.submitted_by_username ? 'pointer' : 'default',
                      '&:hover': photo.submitted_by_username ? { textDecoration: 'underline' } : undefined,
                    }}
                  >
                    {photo.submitted_by_name}
                  </Typography>
                </>
              )}
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              onClick={() => onOpenEvent(momentInfo || { event_id: photo.event_id })}
              sx={{ display: 'block', lineHeight: 1.1, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            >
              {photo.event_name}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={0.25} alignItems="center">
          <IconButton size="small" onClick={() => onToggleBookmark(photo.event_id, photo.photo_id)} aria-label="Bookmark photo">
            {bookmarked ? <BookmarkIcon fontSize="small" color="primary" /> : <BookmarkBorderIcon fontSize="small" />}
          </IconButton>
          <Tooltip title="View in event">
            <IconButton size="small" onClick={() => onOpenEvent(momentInfo || { event_id: photo.event_id })} aria-label="View in event">
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Box
        onClick={handlePhotoTap}
        sx={{
          cursor: 'pointer',
          bgcolor: 'grey.100',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          WebkitTapHighlightColor: 'transparent',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          WebkitTouchCallout: 'none',
        }}
      >
        {previewUrl || thumbnailUrl ? (
          <BlurUpPhoto
            thumbnailUrl={thumbnailUrl}
            src={previewUrl}
            alt=""
            sx={{
              width: '100%',
              maxHeight: 560,
              objectFit: 'contain',
              display: 'block',
              bgcolor: 'grey.100',
              WebkitUserDrag: 'none',
              userSelect: 'none',
              WebkitTouchCallout: 'none',
            }}
          />
        ) : (
          <Skeleton variant="rectangular" animation="wave" sx={{ width: '100%', height: 320 }} />
        )}
        <AnimatePresence>
          {heartBurst && (
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1.15 }}
              exit={{ opacity: 0, scale: 1.3 }}
              transition={{ duration: 0.35 }}
              style={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }}
            >
              <FavoriteIcon sx={{ fontSize: 96, color: 'rgba(255,255,255,0.92)', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.45))' }} />
            </motion.div>
          )}
        </AnimatePresence>
      </Box>

      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ px: 0.75, py: 0.5 }}>
        <IconButton
          size="small"
          onClick={() => onToggleLike(photo.event_id, photo.photo_id)}
          // Mouse-only quick preview (Pointer Events, not onMouseEnter, so a
          // mobile tap's synthetic mouseenter replay can't trigger it too).
          // Touch users get the full paginated list via the count instead —
          // a long-press preview that vanishes on release isn't useful once
          // like counts run into the hundreds.
          onPointerEnter={(e) => { if (e.pointerType === 'mouse') openLikers(e.currentTarget); }}
          onPointerLeave={(e) => { if (e.pointerType === 'mouse') setLikersAnchor(null); }}
          aria-label="Like photo"
        >
          {liked ? <FavoriteIcon fontSize="small" color="error" /> : <FavoriteBorderIcon fontSize="small" />}
        </IconButton>
        {loaded ? (
          <Typography
            variant="caption"
            fontWeight={700}
            onClick={() => { if (likeCount > 0) setLikersDialogOpen(true); }}
            sx={{ minWidth: 14, cursor: likeCount > 0 ? 'pointer' : 'default' }}
          >
            {likeCount}
          </Typography>
        ) : (
          <Skeleton variant="text" width={14} />
        )}

        <IconButton size="small" onClick={() => setCommentsOpen((v) => !v)} aria-label="Toggle comments" sx={{ ml: 0.5 }}>
          <ChatBubbleOutlineIcon fontSize="small" />
        </IconButton>
        {loaded ? (
          <Typography variant="caption" fontWeight={700} sx={{ minWidth: 14 }}>{commentCount}</Typography>
        ) : (
          <Skeleton variant="text" width={14} />
        )}

        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={handleDownload} aria-label="Download photo">
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Stack>

      <LikersPopover anchorEl={likersAnchor} onClose={() => setLikersAnchor(null)} likers={likers || []} loading={likersLoading} />
      <LikersDialog
        open={likersDialogOpen}
        onClose={() => setLikersDialogOpen(false)}
        eventId={photo.event_id}
        photoId={photo.photo_id}
        totalHint={likeCount}
      />

      {commentsOpen && (
        <CommentsPanel
          eventId={photo.event_id}
          photoId={photo.photo_id}
          onCommentCountDelta={(delta) => onCommentCountDelta(photo.event_id, photo.photo_id, delta)}
        />
      )}
    </Paper>
  );
}

function FeedCardSkeleton() {
  return (
    <Paper variant="outlined" sx={{ borderRadius: { xs: 0, sm: 2 }, overflow: 'hidden' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1, py: 0.75 }}>
        <Skeleton variant="circular" width={30} height={30} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="text" width="60%" sx={{ fontSize: '0.7rem' }} />
        </Box>
      </Stack>
      <Skeleton variant="rectangular" animation="wave" sx={{ width: '100%', height: 320 }} />
      <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 0.75, py: 0.5 }}>
        <Skeleton variant="circular" width={24} height={24} />
        <Skeleton variant="circular" width={24} height={24} />
        <Box sx={{ flex: 1 }} />
        <Skeleton variant="circular" width={24} height={24} />
      </Stack>
    </Paper>
  );
}

function FeedPhotoView({
  photos, visibleCount, onLoadMore, onPhotoClick, hasFilters, onReset,
  searchActive, searchQuery, presignedUrls, interactions, momentsById,
  onOpenEvent, onToggleLike, onToggleBookmark, onCommentCountDelta,
}) {
  const visiblePhotos = photos.slice(0, visibleCount);
  const remaining = photos.length - visibleCount;
  const sentinelRef = useRef(null);

  // Infinite scroll: trigger the next batch while the sentinel is still
  // ~5 cards below the viewport (rootMargin), instead of waiting for the
  // user to hit a "Load more" button or the literal bottom of the page.
  useEffect(() => {
    if (remaining <= 0) return undefined;
    const node = sentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) onLoadMore(); },
      { rootMargin: '3500px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [remaining, onLoadMore]);

  if (!photos.length) {
    return <EmptyMoments hasFilters={hasFilters} onReset={onReset} />;
  }

  return (
    <Box sx={{ mt: 3 }}>
      {searchActive && searchQuery && (
        <Typography variant="caption" color="primary" sx={{ display: 'block', ml: 0.5, mb: 1.5, maxWidth: 470, mx: 'auto' }}>
          Showing top matches for <strong>{searchQuery}</strong>
        </Typography>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: 2,
          maxWidth: 470,
          mx: 'auto',
        }}
      >
        {visiblePhotos.map((photo) => (
          <FeedCard
            key={`${photo.event_id}-${photo.photo_id}`}
            photo={photo}
            momentInfo={momentsById[photo.event_id]}
            previewUrl={presignedMediaUrl(presignedUrls, 'previews', photo.event_id, photo.photo_id)}
            thumbnailUrl={photo.thumbnail_url || presignedMediaUrl(presignedUrls, 'thumbnails', photo.event_id, photo.photo_id)}
            interaction={interactions[photoKey(photo.event_id, photo.photo_id)]}
            onPhotoClick={onPhotoClick}
            onOpenEvent={onOpenEvent}
            onToggleLike={onToggleLike}
            onToggleBookmark={onToggleBookmark}
            onCommentCountDelta={onCommentCountDelta}
          />
        ))}
        {remaining > 0 && (
          <>
            <Box ref={sentinelRef} sx={{ height: 1, gridColumn: '1 / -1' }} />
            <FeedCardSkeleton />
          </>
        )}
      </Box>
    </Box>
  );
}

const PHOTO_ZOOM_MIN = 1;
const PHOTO_ZOOM_MAX = 4;

function clampPhotoOffset(offset, scale, size) {
  if (scale <= 1 || !size.width || !size.height) return { x: 0, y: 0 };
  const maxX = (size.width * (scale - 1)) / 2;
  const maxY = (size.height * (scale - 1)) / 2;
  return {
    x: Math.min(maxX, Math.max(-maxX, offset.x)),
    y: Math.min(maxY, Math.max(-maxY, offset.y)),
  };
}

function ZoomPanImage({ src, placeholderSrc, alt = '', sx }) {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const lastTapRef = useRef(0);

  // Show whatever's already on hand (a thumbnail/preview the feed already
  // rendered) instantly, then swap to the full-resolution src in the
  // background once it's decoded - avoids a blank/skeleton wait on a fetch
  // whose result we usually already have cached from the feed.
  const [displaySrc, setDisplaySrc] = useState(() => src || placeholderSrc || '');
  const [isPlaceholder, setIsPlaceholder] = useState(() => Boolean(!src && placeholderSrc));

  useEffect(() => {
    if (!src) {
      setDisplaySrc(placeholderSrc || '');
      setIsPlaceholder(Boolean(placeholderSrc));
      return undefined;
    }
    if (placeholderSrc && placeholderSrc !== src) {
      setDisplaySrc(placeholderSrc);
      setIsPlaceholder(true);
    } else {
      setIsPlaceholder(false);
    }
    let cancelled = false;
    const preload = new Image();
    preload.onload = () => {
      if (!cancelled) {
        setDisplaySrc(src);
        setIsPlaceholder(false);
      }
    };
    preload.src = src;
    return () => {
      cancelled = true;
    };
  }, [src, placeholderSrc]);

  useEffect(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, [src]);

  const getSize = () => {
    const el = containerRef.current;
    if (!el) return { width: 0, height: 0 };
    const rect = el.getBoundingClientRect();
    return { width: rect.width, height: rect.height };
  };

  const resetZoom = () => {
    setScale(PHOTO_ZOOM_MIN);
    setOffset({ x: 0, y: 0 });
  };

  const handlePointerDown = (e) => {
    containerRef.current?.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 2) {
      const pts = Array.from(pointers.current.values());
      gesture.current = {
        mode: 'pinch',
        startDistance: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y),
        startScale: scale,
        startOffset: offset,
      };
    } else if (pointers.current.size === 1) {
      const now = Date.now();
      if (now - lastTapRef.current < 300) {
        lastTapRef.current = 0;
        if (scale > 1) resetZoom();
        else setScale(2);
        gesture.current = null;
        return;
      }
      lastTapRef.current = now;
      if (scale > 1) {
        gesture.current = {
          mode: 'pan',
          startPointer: { x: e.clientX, y: e.clientY },
          startOffset: offset,
        };
      }
    }
  };

  const handlePointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (gesture.current?.mode === 'pinch' && pointers.current.size === 2) {
      const pts = Array.from(pointers.current.values());
      const distance = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      const ratio = gesture.current.startDistance ? distance / gesture.current.startDistance : 1;
      const nextScale = Math.min(PHOTO_ZOOM_MAX, Math.max(PHOTO_ZOOM_MIN, gesture.current.startScale * ratio));
      setScale(nextScale);
      setOffset(clampPhotoOffset(gesture.current.startOffset, nextScale, getSize()));
    } else if (gesture.current?.mode === 'pan' && pointers.current.size === 1) {
      const dx = e.clientX - gesture.current.startPointer.x;
      const dy = e.clientY - gesture.current.startPointer.y;
      const nextOffset = {
        x: gesture.current.startOffset.x + dx,
        y: gesture.current.startOffset.y + dy,
      };
      setOffset(clampPhotoOffset(nextOffset, scale, getSize()));
    }
  };

  const endPointer = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size === 0) {
      gesture.current = null;
    } else if (pointers.current.size === 1) {
      const [remaining] = pointers.current.values();
      gesture.current = scale > 1 ? { mode: 'pan', startPointer: remaining, startOffset: offset } : null;
    }
  };

  return (
    <Box
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
      onPointerLeave={endPointer}
      onDoubleClick={() => (scale > 1 ? resetZoom() : setScale(2))}
      sx={{ position: 'relative', overflow: 'hidden', touchAction: 'none', lineHeight: 0 }}
    >
      <Box
        component="img"
        src={displaySrc}
        alt={alt}
        draggable={false}
        sx={{
          ...sx,
          display: 'block',
          objectFit: 'contain',
          transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
          transformOrigin: 'center center',
          transition: gesture.current ? 'none' : 'transform 0.15s ease-out, filter 0.2s ease-out',
          filter: isPlaceholder ? 'blur(12px)' : 'none',
          cursor: scale > 1 ? 'grab' : 'default',
          userSelect: 'none',
        }}
      />
    </Box>
  );
}

function PhotoDialog({ selection, onClose, onOpenEvent, previewUrl, placeholderUrl }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [downloadAnchor, setDownloadAnchor] = useState(null);
  const [moreAnchor, setMoreAnchor] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [ucAvailable, setUcAvailable] = useState(false);
  const [ucDownloading, setUcDownloading] = useState(false);
  const [showcased, setShowcased] = useState(false);
  const [showcaseLoading, setShowcaseLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'info' });
  const toast = useCallback((msg, sev = 'info') => setSnack({ open: true, msg: String(msg), sev }), []);

  useEffect(() => { setInfoOpen(false); }, [selection]);

  useEffect(() => {
    setUcAvailable(false);
    if (!selection) return;
    let cancelled = false;
    momentsApi.getUcOriginalStatus(selection.moment.event_id, selection.photoId)
      .then(d => { if (!cancelled) setUcAvailable(Boolean(d?.available)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selection]);

  useEffect(() => {
    setShowcased(false);
    if (!selection) return;
    let cancelled = false;
    profileApi.getMyShowcasePhotos()
      .then(d => {
        if (cancelled) return;
        const isShowcased = (d?.photos || []).some(
          p => p.event_id === selection.moment.event_id && p.photo_id === selection.photoId,
        );
        setShowcased(isShowcased);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selection]);

  async function toggleShowcase() {
    if (!selection || showcaseLoading) return;
    const { event_id: eventId } = selection.moment;
    setShowcaseLoading(true);
    try {
      if (showcased) {
        await profileApi.removeShowcasePhoto(eventId, selection.photoId);
        setShowcased(false);
      } else {
        await profileApi.addShowcasePhoto(eventId, selection.photoId);
        setShowcased(true);
      }
    } catch {
      // non-fatal - button stays at its prior state
    } finally {
      setShowcaseLoading(false);
    }
  }

  if (!selection) return null;

  function openDownload(url) {
    setDownloadAnchor(null);
    downloadUrlAsBlob(url, `circlemoments-${selection.moment.event_id}-${selection.photoId}.jpg`)
      .catch((err) => toast(err.message || 'Download failed.', 'error'));
  }

  function downloadUcOriginalPhoto() {
    if (ucDownloading) return;
    setDownloadAnchor(null);
    const { event_id: eventId } = selection.moment;
    downloadUcOriginal(momentsApi.requestUcOriginalDownload, eventId, selection.photoId, {
      filename: `circlemoments-${eventId}-${selection.photoId}-uncompressed.jpg`,
      onActiveChange: setUcDownloading,
    }).catch((err) => toast(err.message || 'Download failed.', 'error'));
  }

  function shareThisPhoto() {
    sharePhoto(
      momentsApi.originalUrl(selection.moment.event_id, selection.photoId),
      `circlemoments-${selection.moment.event_id}-${selection.photoId}.jpg`,
      { title: selection.moment.event_name, text: `Photo from ${selection.moment.event_name || 'this event'} on Circle Moments` },
    ).catch((err) => toast(err.message || 'Share failed.', 'error'));
  }

  return (
    <>
      <UcDownloadProgressBar active={ucDownloading} />
      <Dialog open onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 0, bgcolor: 'grey.950' }}>
          <Box sx={{ position: 'relative' }}>
            <IconButton
              size={isMobile ? 'small' : 'medium'}
              onClick={onClose}
              sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, bgcolor: 'rgba(255,255,255,0.9)' }}
            >
              <CloseIcon fontSize={isMobile ? 'small' : 'medium'} />
            </IconButton>
            <Tooltip title={infoOpen ? 'Hide photo info' : 'Photo info'}>
              <IconButton
                size={isMobile ? 'small' : 'medium'}
                onClick={() => setInfoOpen((v) => !v)}
                sx={{
                  position: 'absolute', top: 8, left: 8, zIndex: 1,
                  bgcolor: infoOpen ? 'primary.main' : 'rgba(255,255,255,0.9)',
                  color: infoOpen ? 'common.white' : 'inherit',
                }}
              >
                <InfoOutlinedIcon fontSize={isMobile ? 'small' : 'medium'} />
              </IconButton>
            </Tooltip>
            {previewUrl || placeholderUrl ? (
              <ZoomPanImage
                src={previewUrl}
                placeholderSrc={placeholderUrl}
                alt=""
                sx={{ width: '100%', maxHeight: '72vh', bgcolor: '#111' }}
              />
            ) : (
              <Skeleton
                variant="rectangular"
                animation="wave"
                sx={{ width: '100%', height: '60vh', bgcolor: 'grey.800' }}
              />
            )}
          </Box>
        </DialogContent>
        <Collapse in={infoOpen}>
          <PhotoInfoPanel photo={selection.moment} sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />
        </Collapse>
        <DialogActions
          sx={{
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between', px: 2, py: 1.5, gap: 1,
          }}
        >
          <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
            <Typography variant="subtitle2" fontWeight={800}>{selection.moment.event_name}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{formatDate(selection.moment.event_date)}</Typography>
            {selection.moment.submitted_by_name && (
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                onClick={selection.moment.submitted_by_username ? () => navigate(`/profile/${selection.moment.submitted_by_username}`) : undefined}
                sx={{
                  cursor: selection.moment.submitted_by_username ? 'pointer' : 'default',
                  '&:hover .submitted-by-name': selection.moment.submitted_by_username ? { textDecoration: 'underline' } : undefined,
                }}
              >
                <Avatar src={selection.moment.submitted_by_avatar || undefined} sx={{ width: 16, height: 16, fontSize: 10 }}>
                  {selection.moment.submitted_by_name.slice(0, 1).toUpperCase()}
                </Avatar>
                <Typography variant="caption" color="text.secondary" className="submitted-by-name">
                  Submitted by {selection.moment.submitted_by_name}
                </Typography>
              </Stack>
            )}
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ justifyContent: { xs: 'center', sm: 'flex-end' } }}>
            <Tooltip title="Share photo">
              <IconButton size="small" onClick={shareThisPhoto}>
                <IosShareIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download original">
              <IconButton
                size="small"
                onClick={() => openDownload(momentsApi.originalUrl(selection.moment.event_id, selection.photoId))}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="More download options">
              <IconButton size="small" onClick={(e) => setDownloadAnchor(e.currentTarget)}>
                <ArrowDropDownIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={downloadAnchor} open={Boolean(downloadAnchor)} onClose={() => setDownloadAnchor(null)}>
              <MenuItem onClick={() => openDownload(momentsApi.originalUrl(selection.moment.event_id, selection.photoId))}>
                <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Download original</ListItemText>
              </MenuItem>
              <MenuItem
                disabled={!ucAvailable || ucDownloading}
                onClick={downloadUcOriginalPhoto}
              >
                <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Download uncompressed original</ListItemText>
              </MenuItem>
            </Menu>
            {isMobile ? (
              <>
                <Tooltip title="More options">
                  <IconButton size="small" onClick={(e) => setMoreAnchor(e.currentTarget)}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}>
                  <MenuItem disabled={showcaseLoading} onClick={() => { setMoreAnchor(null); toggleShowcase(); }}>
                    <ListItemIcon>
                      {showcaseLoading ? <CircularProgress size={16} /> : (showcased ? <CheckCircleIcon fontSize="small" /> : <AddAPhotoIcon fontSize="small" />)}
                    </ListItemIcon>
                    <ListItemText>{showcased ? 'Remove from profile' : 'Add to Profile View'}</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => { setMoreAnchor(null); onOpenEvent(selection.moment); }}>
                    <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Organise in event</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <>
                <Tooltip title={showcased ? 'Remove from your public profile grid' : 'Show this photo on your public profile'}>
                  <span>
                    <Button
                      variant={showcased ? 'contained' : 'outlined'}
                      color={showcased ? 'success' : 'primary'}
                      size="small"
                      startIcon={showcaseLoading ? <CircularProgress size={14} color="inherit" /> : (showcased ? <CheckCircleIcon /> : <AddAPhotoIcon />)}
                      onClick={toggleShowcase}
                      disabled={showcaseLoading}
                      sx={{ textTransform: 'none' }}
                    >
                      {showcased ? 'On Profile' : 'Add to Profile View'}
                    </Button>
                  </span>
                </Tooltip>
                <Button variant="contained" endIcon={<OpenInNewIcon />} onClick={() => onOpenEvent(selection.moment)} sx={{ textTransform: 'none' }}>
                  Organise in event
                </Button>
              </>
            )}
          </Stack>
        </DialogActions>
      </Dialog>
      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.sev} variant="filled" onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </>
  );
}

export default function Moments() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // Filter state
  const [lens, setLens] = useState('all');
  const [circleIds, setCircleIds] = useState([]);
  const [includeHidden, setIncludeHidden] = useState(false);
  const [specificEventIds, setSpecificEventIds] = useState([]);
  const [specificPickerOpen, setSpecificPickerOpen] = useState(false);
  const [specificPickerMoments, setSpecificPickerMoments] = useState([]);
  const [specificPickerLoading, setSpecificPickerLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSearchMode, setSelectedSearchMode] = useState('plan');
  const [sortMode, setSortMode] = useState('recent');
  const [bookmarkedOnly, setBookmarkedOnly] = useState(false);

  // View mode
  const [viewMode, setViewMode] = useState('feeds');

  // Event view state
  const [moments, setMoments] = useState([]);
  const [circles, setCircles] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchActive, setSearchActive] = useState(false);
  const [searchModeLabel, setSearchModeLabel] = useState('');
  const [searchModeNote, setSearchModeNote] = useState('');
  const [visibleEventCount, setVisibleEventCount] = useState(5);
  const [photoLimits, setPhotoLimits] = useState({});
  const [eventPresignedUrls, setEventPresignedUrls] = useState({ thumbnails: {}, previews: {} });

  // Grid view state
  const [gridPhotos, setGridPhotos] = useState([]);
  const [gridLoading, setGridLoading] = useState(false);
  const [visibleGridCount, setVisibleGridCount] = useState(10);
  const [gridSearchActive, setGridSearchActive] = useState(false);
  const [gridSearchModeLabel, setGridSearchModeLabel] = useState('');
  const [gridSearchModeNote, setGridSearchModeNote] = useState('');
  const [gridPresignedUrls, setGridPresignedUrls] = useState({ thumbnails: {}, previews: {} });
  const [previewPresignedUrls, setPreviewPresignedUrls] = useState({ thumbnails: {}, previews: {} });

  // Feeds view state
  const [feedInteractions, setFeedInteractions] = useState({});

  const [invalidatingCache, setInvalidatingCache] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Photo preview
  const [photoSelection, setPhotoSelection] = useState(null);

  const hasFilters = lens !== 'all' || circleIds.length > 0 || includeHidden || searchQuery || bookmarkedOnly || specificEventIds.length > 0;

  const loadMoments = useCallback(async (overrideQ, options = {}) => {
    setLoading(true);
    setError('');
    setVisibleEventCount(5);
    setPhotoLimits({});
    const q = options.skipSearch ? '' : (overrideQ !== undefined ? overrideQ : searchQuery);
    try {
      const payload = await momentsApi.getMoments({ lens, circleIds, includeHidden, q, searchMode: selectedSearchMode, eventIds: specificEventIds });
      setMoments(Array.isArray(payload.moments) ? payload.moments : []);
      setCircles(Array.isArray(payload.circles) ? payload.circles : []);
      setStats(payload.stats || {});
      setSearchActive(Boolean(payload.search_active));
      setSearchModeLabel(payload.search_mode_label || '');
      setSearchModeNote(payload.search_mode_note || '');
    } catch (err) {
      setError(err.message || 'Failed to load Moments');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lens, circleIds, includeHidden, searchQuery, selectedSearchMode, specificEventIds]);

  const loadGridPhotos = useCallback(async (overrideQ) => {
    setGridLoading(true);
    setError('');
    setVisibleGridCount(10);
    const q = overrideQ !== undefined ? overrideQ : searchQuery;
    try {
      const payload = await momentsApi.getPhotos({
        lens, circleIds, includeHidden, q, searchMode: selectedSearchMode,
        sort: sortMode, bookmarkedOnly, eventIds: specificEventIds,
      });
      setGridPhotos(Array.isArray(payload.photos) ? payload.photos : []);
      setGridSearchActive(Boolean(payload.search_active));
      setGridSearchModeLabel(payload.search_mode_label || '');
      setGridSearchModeNote(payload.search_mode_note || '');
    } catch (err) {
      setError(err.message || 'Failed to load photos');
    } finally {
      setGridLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lens, circleIds, includeHidden, searchQuery, selectedSearchMode, sortMode, bookmarkedOnly, specificEventIds]);

  const handleToggleSearchMode = useCallback(() => {
    setSelectedSearchMode((current) => (current === 'legacy' ? 'plan' : 'legacy'));
  }, []);

  const handleSubmitSearch = useCallback((query) => {
    setSearchQuery(query);
    if (!query) {
      setSearchActive(false);
      setGridSearchActive(false);
      setSearchModeLabel('');
      setSearchModeNote('');
      setGridSearchModeLabel('');
      setGridSearchModeNote('');
    }
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchActive(false);
    setGridSearchActive(false);
    setSearchModeLabel('');
    setSearchModeNote('');
    setGridSearchModeLabel('');
    setGridSearchModeNote('');
  }, []);

  const handleResetFilters = () => {
    setLens('all');
    setCircleIds([]);
    setIncludeHidden(false);
    setBookmarkedOnly(false);
    setSpecificEventIds([]);
    handleClearSearch();
  };

  const handleOpenSpecificEventsPicker = useCallback(() => {
    setSpecificPickerOpen(true);
    setSpecificPickerLoading(true);
    momentsApi.getMoments({ lens: 'all', circleIds: [], includeHidden })
      .then((payload) => setSpecificPickerMoments(Array.isArray(payload.moments) ? payload.moments : []))
      .catch(() => setSpecificPickerMoments([]))
      .finally(() => setSpecificPickerLoading(false));
  }, [includeHidden]);

  const handleConfirmSpecificEvents = (selectedIds) => {
    setSpecificPickerOpen(false);
    setSpecificEventIds(selectedIds);
    setLens(selectedIds.length ? 'specific' : 'all');
  };

  // Always load event data (for stats/circles/filters) — the events search is
  // redundant whenever the photo list itself comes from loadGridPhotos (grid
  // and feeds both search there instead), so skip it for those two view modes.
  useEffect(() => {
    loadMoments(undefined, { skipSearch: viewMode !== 'events' });
  }, [loadMoments, viewMode]);

  // Load grid photos in grid or feeds mode (or when filters/sort change)
  useEffect(() => {
    if (viewMode === 'grid' || viewMode === 'feeds') loadGridPhotos();
  }, [viewMode, loadGridPhotos]);

  const visibleMoments = useMemo(() => moments.slice(0, visibleEventCount), [moments, visibleEventCount]);

  const momentsById = useMemo(() => {
    const map = {};
    for (const m of moments) map[m.event_id] = m;
    return map;
  }, [moments]);

  useEffect(() => {
    if (viewMode !== 'events') {
      return undefined;
    }

    const photos = visibleMoments.flatMap((moment) =>
      (moment.photo_ids || moment.sample_photo_ids || [])
        .slice(0, photoLimits[moment.event_id] || 20)
        .map((photoId) => ({ event_id: moment.event_id, photo_id: photoId }))
    );
    const missingPhotos = missingMediaPhotos(photos, eventPresignedUrls, 'thumbnails');

    if (!missingPhotos.length) {
      return;
    }

    let cancelled = false;
    momentsApi.getPresignedUrls(missingPhotos, ['thumbnails'])
      .then((payload) => {
        if (!cancelled) setEventPresignedUrls((prev) => mergeMediaUrls(prev, payload));
      })
      .catch(() => {
        if (!cancelled) setEventPresignedUrls((prev) => prev);
      });
    return () => {
      cancelled = true;
    };
  }, [viewMode, visibleMoments, photoLimits, eventPresignedUrls]);

  useEffect(() => {
    if (viewMode !== 'grid') return undefined;
    const photos = gridPhotos
      .slice(0, visibleGridCount)
      .filter((photo) => !photo.thumbnail_url)
      .map((photo) => ({ event_id: photo.event_id, photo_id: photo.photo_id }));
    const missingPhotos = missingMediaPhotos(photos, gridPresignedUrls, 'thumbnails');

    if (!missingPhotos.length) {
      return undefined;
    }

    let cancelled = false;
    momentsApi.getPresignedUrls(missingPhotos, ['thumbnails'])
      .then((payload) => {
        if (!cancelled) setGridPresignedUrls((prev) => mergeMediaUrls(prev, payload));
      })
      .catch(() => {
        if (!cancelled) setGridPresignedUrls((prev) => prev);
      });
    return () => {
      cancelled = true;
    };
  }, [viewMode, gridPhotos, visibleGridCount, gridPresignedUrls]);

  // Feeds view loads both the thumbnail (used as a blur-up placeholder while
  // the full preview downloads) and the full preview resolution for each
  // card, same batched presigned-URL pattern as the grid effect above.
  useEffect(() => {
    if (viewMode !== 'feeds') return undefined;
    const photos = gridPhotos
      .slice(0, visibleGridCount)
      .map((photo) => ({ event_id: photo.event_id, photo_id: photo.photo_id }));
    const missingPhotos = missingMediaPhotos(photos, gridPresignedUrls, 'previews');

    if (!missingPhotos.length) {
      return undefined;
    }

    let cancelled = false;
    momentsApi.getPresignedUrls(missingPhotos, ['thumbnails', 'previews'])
      .then((payload) => {
        if (!cancelled) setGridPresignedUrls((prev) => mergeMediaUrls(prev, payload));
      })
      .catch(() => {
        if (!cancelled) setGridPresignedUrls((prev) => prev);
      });
    return () => {
      cancelled = true;
    };
  }, [viewMode, gridPhotos, visibleGridCount, gridPresignedUrls]);

  // Feeds view: batch-load like/comment counts + my bookmark/like state,
  // asynchronously and separately from the photo list itself (same batching
  // pattern as the presigned-thumbnail effect above).
  useEffect(() => {
    if (viewMode !== 'feeds') return undefined;
    const photos = gridPhotos.slice(0, visibleGridCount);
    const missing = photos.filter((photo) => !(photoKey(photo.event_id, photo.photo_id) in feedInteractions));

    if (!missing.length) {
      return undefined;
    }

    let cancelled = false;
    momentsApi.getInteractionCounts(missing.map((p) => ({ event_id: p.event_id, photo_id: p.photo_id })))
      .then((payload) => {
        if (!cancelled) setFeedInteractions((prev) => ({ ...prev, ...(payload.counts || {}) }));
      })
      .catch(() => {
        if (!cancelled) setFeedInteractions((prev) => prev);
      });
    return () => {
      cancelled = true;
    };
  }, [viewMode, gridPhotos, visibleGridCount, feedInteractions]);

  const handleToggleLike = useCallback((eventId, photoId) => {
    const key = photoKey(eventId, photoId);
    let snapshot;
    setFeedInteractions((prev) => {
      snapshot = prev[key];
      const cur = prev[key] || { like_count: 0, comment_count: 0, liked_by_me: false, bookmarked_by_me: false };
      const nextLiked = !cur.liked_by_me;
      return { ...prev, [key]: { ...cur, liked_by_me: nextLiked, like_count: Math.max(0, cur.like_count + (nextLiked ? 1 : -1)) } };
    });
    momentsApi.toggleLike(eventId, photoId)
      .then((result) => {
        setFeedInteractions((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), liked_by_me: result.liked, like_count: result.like_count } }));
      })
      .catch(() => {
        setFeedInteractions((prev) => ({ ...prev, [key]: snapshot || prev[key] }));
      });
  }, []);

  const handleToggleBookmark = useCallback((eventId, photoId) => {
    const key = photoKey(eventId, photoId);
    let snapshot;
    setFeedInteractions((prev) => {
      snapshot = prev[key];
      const cur = prev[key] || { like_count: 0, comment_count: 0, liked_by_me: false, bookmarked_by_me: false };
      return { ...prev, [key]: { ...cur, bookmarked_by_me: !cur.bookmarked_by_me } };
    });
    momentsApi.toggleBookmark(eventId, photoId)
      .then((result) => {
        setFeedInteractions((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), bookmarked_by_me: result.bookmarked } }));
      })
      .catch(() => {
        setFeedInteractions((prev) => ({ ...prev, [key]: snapshot || prev[key] }));
      });
  }, []);

  const handleCommentCountDelta = useCallback((eventId, photoId, delta) => {
    const key = photoKey(eventId, photoId);
    setFeedInteractions((prev) => {
      const cur = prev[key] || { like_count: 0, comment_count: 0, liked_by_me: false, bookmarked_by_me: false };
      return { ...prev, [key]: { ...cur, comment_count: Math.max(0, cur.comment_count + delta) } };
    });
  }, []);

  // The feed/grid views already fetch thumbnails (always) and often previews
  // (feed view pre-loads both) into their own caches before a photo is even
  // clickable, so check those first and only hit the backend for a fresh
  // preview URL when none of them already has one.
  const findKnownPhotoUrl = useCallback((type, eventId, photoId) => (
    presignedMediaUrl(previewPresignedUrls, type, eventId, photoId)
    || presignedMediaUrl(gridPresignedUrls, type, eventId, photoId)
    || presignedMediaUrl(eventPresignedUrls, type, eventId, photoId)
  ), [previewPresignedUrls, gridPresignedUrls, eventPresignedUrls]);

  useEffect(() => {
    if (!photoSelection) return undefined;
    const eventId = photoSelection.moment.event_id;
    const photoId = photoSelection.photoId;
    if (findKnownPhotoUrl('previews', eventId, photoId)) {
      return undefined;
    }

    let cancelled = false;
    momentsApi.getPresignedUrls([{ event_id: eventId, photo_id: photoId }], ['previews'])
      .then((payload) => {
        if (!cancelled) setPreviewPresignedUrls((prev) => mergeMediaUrls(prev, payload));
      })
      .catch(() => {
        if (!cancelled) setPreviewPresignedUrls((prev) => prev);
      });
    return () => {
      cancelled = true;
    };
  }, [photoSelection, findKnownPhotoUrl]);

  const monthGroups = useMemo(() => {
    const groups = [];
    for (const moment of visibleMoments) {
      const label = formatMonth(moment.event_date);
      const existing = groups.find((group) => group.label === label);
      if (existing) existing.items.push(moment);
      else groups.push({ label, items: [moment] });
    }
    return groups;
  }, [visibleMoments]);

  const handleHide = async (moment) => {
    try {
      await momentsApi.updateVisibility(moment.event_id, { hidden: !moment.hidden, pinned: moment.pinned, note: moment.note });
      loadMoments();
    } catch (err) {
      setError(err.message || 'Failed to update moment visibility');
    }
  };

  const handleShowMorePhotos = (eventId, total) => {
    setPhotoLimits((prev) => ({
      ...prev,
      [eventId]: Math.min((prev[eventId] || 20) + 20, total),
    }));
  };

  const openEvent = (moment) => navigate(`/event/${moment.event_id}`);

  const handleRefresh = () => {
    if (viewMode === 'events') loadMoments();
    else loadGridPhotos();
  };

  const handleInvalidateCache = async () => {
    setInvalidatingCache(true);
    try {
      await momentsApi.invalidateCache();
    } catch {
      // best-effort; no visible error needed for a cache flush
    } finally {
      setInvalidatingCache(false);
    }
  };

  const isLoading = viewMode === 'events' ? loading : gridLoading;
  const noSearchMatches = Boolean(searchQuery) && (
    viewMode === 'events'
      ? !loading && !searchActive
      : !gridLoading && !gridSearchActive
  );
  const filterCount = (lens !== 'all' ? 1 : 0) + circleIds.length + (includeHidden ? 1 : 0) + (bookmarkedOnly ? 1 : 0);

  const statsSection = (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 1.5 }}>
      <StatTile icon={<EventIcon fontSize="small" />} label="Events" value={stats.total_events || 0} />
      <StatTile icon={<PhotoLibraryIcon fontSize="small" />} label="Photos visible" value={stats.photo_count || 0} />
      <StatTile icon={<GroupsIcon fontSize="small" />} label="Circles" value={stats.circle_count || 0} />
      <StatTile icon={<HowToRegIcon fontSize="small" />} label="Organised" value={stats.organised_events || 0} />
      <StatTile icon={<PersonSearchIcon fontSize="small" />} label="Requests" value={stats.request_count || 0} />
    </Box>
  );

  const filtersSection = (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.86)' }}>
      <Stack spacing={2}>
        <Box>
          <Typography fontWeight={850} sx={{ mb: 1 }}>Choose a perspective</Typography>
          <LensSelector
            lens={lens}
            onChange={setLens}
            specificCount={specificEventIds.length}
            onSpecificClick={handleOpenSpecificEventsPicker}
          />
        </Box>
        <Divider />
        <Box>
          <Typography fontWeight={850} sx={{ mb: 1 }}>Look through a circle</Typography>
          <CircleSelector circles={circles} selectedIds={circleIds} onChange={setCircleIds} />
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button
            variant={includeHidden ? 'contained' : 'outlined'}
            startIcon={includeHidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
            onClick={() => setIncludeHidden((prev) => !prev)}
            sx={{ textTransform: 'none', borderRadius: 999 }}
          >
            {includeHidden ? 'Showing hidden moments' : 'Hidden moments off'}
          </Button>
          <Button
            variant={bookmarkedOnly ? 'contained' : 'outlined'}
            startIcon={bookmarkedOnly ? <BookmarkIcon /> : <BookmarkBorderIcon />}
            onClick={() => setBookmarkedOnly((prev) => !prev)}
            sx={{ textTransform: 'none', borderRadius: 999 }}
          >
            {bookmarkedOnly ? 'Bookmarked only' : 'Bookmarks'}
          </Button>
          {hasFilters && (
            <Button onClick={handleResetFilters} sx={{ textTransform: 'none', borderRadius: 999 }}>
              Reset view
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );

  const feedContentSection = (
    <>
      {viewMode === 'events' && searchActive && searchQuery && (
        <Box sx={{ mt: 0.5, ml: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <Typography variant="caption" color="primary">
              Showing top matches for <strong>{searchQuery}</strong> - thumbnails ranked by relevance
            </Typography>
            {searchModeLabel && <Chip label={searchModeLabel} size="small" color="primary" variant="outlined" />}
          </Stack>
          {searchModeNote && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.25 }}>
              {searchModeNote}
            </Typography>
          )}
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {noSearchMatches && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No search matches were found for <strong>{searchQuery}</strong>. Showing your general moments instead.
        </Alert>
      )}

      {/* Content area */}
      {isLoading ? (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : viewMode === 'grid' ? (
        <GridPhotoView
          photos={gridPhotos}
          visibleCount={visibleGridCount}
          onLoadMore={() => setVisibleGridCount((v) => v + 10)}
          onPhotoClick={(photo) => setPhotoSelection({ moment: photo, photoId: photo.photo_id })}
          hasFilters={hasFilters}
          onReset={handleResetFilters}
          searchActive={gridSearchActive}
          searchQuery={searchQuery}
          searchModeLabel={gridSearchModeLabel}
          searchModeNote={gridSearchModeNote}
          presignedUrls={gridPresignedUrls}
        />
      ) : viewMode === 'feeds' ? (
        <FeedPhotoView
          photos={gridPhotos}
          visibleCount={visibleGridCount}
          onLoadMore={() => setVisibleGridCount((v) => v + 10)}
          onPhotoClick={(photo) => setPhotoSelection({ moment: photo, photoId: photo.photo_id })}
          hasFilters={hasFilters}
          onReset={handleResetFilters}
          searchActive={gridSearchActive}
          searchQuery={searchQuery}
          presignedUrls={gridPresignedUrls}
          interactions={feedInteractions}
          momentsById={momentsById}
          onOpenEvent={openEvent}
          onToggleLike={handleToggleLike}
          onToggleBookmark={handleToggleBookmark}
          onCommentCountDelta={handleCommentCountDelta}
        />
      ) : moments.length === 0 ? (
        <Box sx={{ mt: 3 }}>
          <EmptyMoments hasFilters={hasFilters} onReset={handleResetFilters} />
        </Box>
      ) : (
        <Box sx={{ mt: 4, position: 'relative' }}>
          {/* Timeline spine */}
          <Box sx={{ position: 'absolute', display: { xs: 'none', md: 'block' }, left: { md: 130 }, top: 8, bottom: 0, width: 2, bgcolor: 'primary.light', opacity: 0.5 }} />
          <Stack spacing={3}>
            {monthGroups.map((group) => (
              <Box key={group.label}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={850} color="primary" sx={{ width: { xs: 'auto', md: 112 }, pl: { xs: 4, md: 0 } }}>
                    {group.label}
                  </Typography>
                  <Divider sx={{ flex: 1 }} />
                </Stack>
                <Stack spacing={2}>
                  <AnimatePresence>
                    {group.items.map((moment) => (
                      <Box
                        key={moment.event_id}
                        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '112px 24px 1fr' }, gap: { xs: 1.5, md: 2 }, alignItems: 'start' }}
                      >
                        <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' }, pt: 1 }}>
                          {formatDate(moment.event_date)}
                        </Typography>
                        <Box sx={{ display: { xs: 'none', md: 'block' }, width: 24, height: 24, borderRadius: '50%', bgcolor: 'background.paper', border: '3px solid', borderColor: moment.role === 'organiser' ? 'primary.main' : 'secondary.main', zIndex: 1, mt: 1 }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', md: 'none' }, mb: 0.75, textAlign: 'center' }}>
                            {formatDate(moment.event_date)}
                          </Typography>
                          <MomentPanel
                            moment={moment}
                            onPhotoClick={(m, photoId) => setPhotoSelection({ moment: m, photoId })}
                            onOpenEvent={openEvent}
                            onHide={handleHide}
                            photoLimit={photoLimits[moment.event_id] || 20}
                            onShowMorePhotos={(total) => handleShowMorePhotos(moment.event_id, total)}
                            presignedUrls={eventPresignedUrls}
                          />
                        </Box>
                      </Box>
                    ))}
                  </AnimatePresence>
                </Stack>
              </Box>
            ))}
          </Stack>

          {/* Event pagination */}
          {moments.length > visibleEventCount && (
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => setVisibleEventCount((v) => v + 5)}
                sx={{ textTransform: 'none', borderRadius: 999 }}
              >
                Load {Math.min(5, moments.length - visibleEventCount)} more events ({moments.length - visibleEventCount} remaining)
              </Button>
            </Box>
          )}
        </Box>
      )}
    </>
  );

  return (
    <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh', pb: isMobile ? 3 : 0 }}>
      <Container maxWidth="lg" sx={{ py: { xs: 2.5, md: 4 } }}>
        {/* Header */}
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} alignItems={{ xs: 'stretch', md: 'flex-start' }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TimelineIcon color="primary" />
              <Typography variant="h4" fontWeight={900} sx={{ fontSize: { xs: '1.5rem', md: undefined } }}>Moments</Typography>
            </Stack>
            <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 560, fontSize: { xs: '0.85rem', md: undefined } }}>
              {isMobile ? 'Your photos and events, all in one timeline.' : 'Your event-based journey across the occasions you organised, attended, requested from, and shared with circles.'}
            </Typography>
          </Box>
          {/* Desktop-only controls: compact text view toggle + invalidate */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            alignSelf={{ md: 'center' }}
            flexWrap="wrap"
            useFlexGap
            sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end' }}
          >
            <ViewModeToggle viewMode={viewMode} onChange={setViewMode} compact />
            <Tooltip title="Invalidate search cache">
              <span>
                <IconButton
                  onClick={handleInvalidateCache}
                  disabled={invalidatingCache}
                  aria-label="Invalidate search cache"
                  sx={{ border: '1px solid', borderColor: 'divider' }}
                >
                  {invalidatingCache
                    ? <CircularProgress size={18} />
                    : <RefreshIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Mobile-only: view toggle on its own line */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 2 }}>
          <ViewModeToggle viewMode={viewMode} onChange={setViewMode} fullWidth />
        </Box>

        {/* Desktop: stats stay above the search bar, always expanded */}
        {!isMobile && (
          <Box sx={{ mt: 2.5 }}>
            {statsSection}
          </Box>
        )}

        {/* Search */}
        {/* pt reserves room for the AI/legacy toggle pill that overlays above the field;
            alignItems="center" then centers the field against the refresh/sort buttons
            by their actual rendered heights instead of guessed margins. */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: isMobile ? 1 : 0, pt: 3 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <MomentsSearchField
              active={searchActive || gridSearchActive}
              onSubmit={handleSubmitSearch}
              onClear={handleClearSearch}
              searchModeLabel={viewMode === 'events' ? searchModeLabel : gridSearchModeLabel}
              selectedSearchMode={selectedSearchMode}
              onToggleSearchMode={handleToggleSearchMode}
              onRefresh={handleRefresh}
            />
          </Box>
          {!isMobile && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="Refresh">
                <IconButton
                  onClick={handleRefresh}
                  aria-label="Refresh"
                  sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <SortMenuButton sortMode={sortMode} onChange={setSortMode} />
            </Stack>
          )}
        </Stack>

        {/* Mobile: sort/filter/invalidate centered below the search bar */}
        {isMobile && (
          <Box sx={{ mt: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
              <SortMenuButton sortMode={sortMode} onChange={setSortMode} />
              <Tooltip title="Show filters">
                <IconButton
                  onClick={() => setMobileFiltersOpen((prev) => !prev)}
                  color={mobileFiltersOpen ? 'primary' : 'default'}
                  aria-label="Toggle filters and stats"
                  sx={{
                    border: '1px solid',
                    borderColor: mobileFiltersOpen ? 'primary.main' : 'divider',
                    bgcolor: mobileFiltersOpen ? 'primary.50' : 'background.paper',
                    position: 'relative',
                  }}
                >
                  <FilterListIcon fontSize="small" />
                  {filterCount > 0 && (
                    <Box
                      component="span"
                      sx={{
                        position: 'absolute',
                        top: -5,
                        right: -5,
                        minWidth: 18,
                        height: 18,
                        px: 0.5,
                        borderRadius: 999,
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        fontSize: 11,
                        fontWeight: 800,
                        lineHeight: '18px',
                      }}
                    >
                      {filterCount}
                    </Box>
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="Invalidate search cache">
                <span>
                  <IconButton
                    onClick={handleInvalidateCache}
                    disabled={invalidatingCache}
                    aria-label="Invalidate search cache"
                    sx={{ border: '1px solid', borderColor: 'divider' }}
                  >
                    {invalidatingCache
                      ? <CircularProgress size={18} />
                      : <RefreshIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
            {mobileFiltersOpen && (
              <Box sx={{ mt: 2 }}>
                <Stack spacing={2}>
                  {statsSection}
                  {filtersSection}
                </Stack>
              </Box>
            )}
          </Box>
        )}
        {isMobile ? (
          feedContentSection
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mt: 3 }}>
            <Box sx={{ width: '30%', flexShrink: 0, position: 'sticky', top: 16 }}>
              {filtersSection}
            </Box>
            <Box sx={{ width: '70%', minWidth: 0 }}>
              {feedContentSection}
            </Box>
          </Box>
        )}
      </Container>

      <SpecificEventsDialog
        open={specificPickerOpen}
        onClose={() => setSpecificPickerOpen(false)}
        moments={specificPickerMoments}
        loading={specificPickerLoading}
        initialSelected={specificEventIds}
        onConfirm={handleConfirmSpecificEvents}
      />

      <PhotoDialog
        selection={photoSelection}
        onClose={() => setPhotoSelection(null)}
        onOpenEvent={openEvent}
        previewUrl={
          photoSelection
            ? findKnownPhotoUrl('previews', photoSelection.moment.event_id, photoSelection.photoId)
            : ''
        }
        placeholderUrl={
          photoSelection
            ? findKnownPhotoUrl('thumbnails', photoSelection.moment.event_id, photoSelection.photoId)
            : ''
        }
      />
    </Box>
  );
}
